import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import api from "../../../../services/api";
import { useStandardCakeFormState } from "../../../../hooks/useStandardCakeFormState";
import StandardCakeFlavors from "./StandardCakeFlavors";
import StandardCakeSizes from "./StandardCakeSizes";
import StandardCakeColors from "./StandardCakeColors";
import StandardCakeSEO from "./StandardCakeSEO";
import StandardCakeOutsideIcing from "./StandardCakeOutsideIcing";

const StandardCakeFormModal = ({
  open,
  onClose,
  onSave,
  cakeData,
  flavorOptions,
  sizeOptions,
  icingOptions,
  colorOptions,
}) => {
  const isEditMode = Boolean(cakeData);

  const {
    formData,
    setFormData,
    handleChange,
    handleKeywordsChange,
    handleSEOChange,
    loading,
    setLoading,
  } = useStandardCakeFormState(cakeData);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        keywords: formData.keywords
          .split(",")
          .map((kw) => kw.trim())
          .filter(Boolean),
        seo: {
          ...formData.seo,
          metaKeywords: formData.seo.metaKeywords
            .split(",")
            .map((kw) => kw.trim())
            .filter(Boolean),
        },
      };

      if (isEditMode) {
        await api.put(`/standard-cakes/${cakeData._id}`, payload);
      } else {
        await api.post("/standard-cakes", payload);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error("Error saving standard cake:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? "Edit Standard Cake" : "Add New Standard Cake"}
      </DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          name="name"
          label="Internal Name"
          fullWidth
          value={formData.name}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="displayName"
          label="Display Name"
          fullWidth
          value={formData.displayName}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="description"
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={formData.description}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="searchableDescription"
          label="Searchable Description"
          fullWidth
          value={formData.searchableDescription}
          onChange={handleChange}
        />

        <StandardCakeFlavors
          formData={formData}
          setFormData={setFormData}
          flavorOptions={flavorOptions}
        />

        {/* Outside Icing Selection */}
        <StandardCakeOutsideIcing
          formData={formData}
          setFormData={setFormData}
          icingOptions={icingOptions}
        />

        <StandardCakeSizes
          formData={formData}
          setFormData={setFormData}
          sizeOptions={sizeOptions}
        />
        <StandardCakeColors
          formData={formData}
          setFormData={setFormData}
          colorOptions={colorOptions}
        />

        <Typography sx={{ mt: 2, mb: 1 }}>Keywords</Typography>
        <TextField
          margin="dense"
          name="keywords"
          label="Keywords (comma-separated)"
          fullWidth
          value={formData.keywords}
          onChange={handleKeywordsChange}
        />

        <StandardCakeSEO formData={formData} setFormData={setFormData} />
      </DialogContent>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.isVisible}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, isVisible: e.target.checked }))
            }
            name="isVisible"
          />
        }
        label="Visible on Website"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.isAvailable}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                isAvailable: e.target.checked,
              }))
            }
            name="isAvailable"
          />
        }
        label="Available for Ordering"
      />

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Saving..." : isEditMode ? "Save Changes" : "Add Cake"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StandardCakeFormModal;
